import { ISeriesApi, ISeriesPrimitive, SeriesAttachedParameter, SeriesOptionsMap, SeriesType, Time, MismatchDirection } from 'lightweight-charts'
import { TimeBucket_TimeSeries } from '../../../proto/generated/common'
import { SuperChartConfiguration_TimeBucket } from '../../../proto/generated/superchart-page'
import { TimeZone, istToUTC } from '../../../util/dateTime'
import { isIntraday } from '../../../util/timeBuckets'
import { getTimeWidthInSeconds } from '../chartHelpers'

function formatCountdown(totalSeconds: number): string {
    if (totalSeconds <= 0) return '0:00'
    const days = Math.floor(totalSeconds / 86400)
    const h = Math.floor((totalSeconds % 86400) / 3600)
    const m = Math.floor((totalSeconds % 3600) / 60)
    const s = totalSeconds % 60
    if (days > 0) {
        return `${days}d ${h}h`
    }
    if (h > 0) {
        return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    }
    return `${m}:${String(s).padStart(2, '0')}`
}

function zonedToUTC(zonedTime: number, timezone: TimeZone): number {
    return timezone === TimeZone.IST ? istToUTC(zonedTime) : zonedTime
}

function getCandleCloseZoned(
    lastBarZoned: number,
    timeBucket: SuperChartConfiguration_TimeBucket
): number {
    const n = Math.max(1, timeBucket.timeValue || 1)
    const series = timeBucket.timeSeries

    if (isIntraday(series)) {
        return lastBarZoned + getTimeWidthInSeconds(timeBucket)
    }

    const d = new Date(lastBarZoned * 1000)
    d.setUTCHours(0, 0, 0, 0)

    switch (series) {
        case TimeBucket_TimeSeries.DAY: {
            d.setUTCDate(d.getUTCDate() + n)
            break
        }
        case TimeBucket_TimeSeries.WEEK: {
            // Weeks start Monday (ISO), matching chart week buckets.
            const day = d.getUTCDay() // 0=Sun … 6=Sat
            const daysFromMonday = (day + 6) % 7
            d.setUTCDate(d.getUTCDate() - daysFromMonday + n * 7)
            break
        }
        case TimeBucket_TimeSeries.MONTH: {
            d.setUTCDate(1)
            d.setUTCMonth(d.getUTCMonth() + n)
            break
        }
        case TimeBucket_TimeSeries.YEAR: {
            d.setUTCMonth(0, 1)
            d.setUTCFullYear(d.getUTCFullYear() + n)
            break
        }
        default: {
            return lastBarZoned + getTimeWidthInSeconds(timeBucket)
        }
    }

    return Math.floor(d.getTime() / 1000)
}

export interface CountdownPrimitiveOptions {
    textColor?: string
    backColor?: string
}

/**
 * Shows a candle-close countdown alongside the current price.
 *
 * Uses the series `title` option — the lightweight-charts "name label" —
 * which prepends text directly onto the floating last-price badge inside
 * the chart pane.  No custom canvas drawing, no hidden labels; fully native
 * rendering that automatically respects all chart settings (overlap
 * avoidance, pane sizing, theme, etc.).
 */
export class CountdownPrimitive implements ISeriesPrimitive<Time> {
    series: ISeriesApi<keyof SeriesOptionsMap> | null = null

    private _requestUpdate: (() => void) | null = null
    private _dataChangedHandler: (() => void) | null = null
    private _timeBucket: SuperChartConfiguration_TimeBucket
    private _timezone: TimeZone
    private _intervalId: ReturnType<typeof setInterval> | null = null
    private _originalTitle = ''
    private _enabled = true
    /** Last title handed to applyOptions; every applyOptions call repaints the whole chart, so skip no-ops. */
    private _lastAppliedTitle: string | null = null

    // kept for API compatibility with callers that pass options
    textColor: string
    backColor: string
    countdownText = ''

    constructor(
        timeBucket: SuperChartConfiguration_TimeBucket,
        timezone: TimeZone = TimeZone.UTC,
        options: CountdownPrimitiveOptions = {},
    ) {
        this._timeBucket = timeBucket
        this._timezone = timezone
        this.textColor = options.textColor ?? '#ffffff'
        this.backColor = options.backColor ?? 'rgba(41, 98, 255, 0.85)'
    }

    updateTimeBucket(timeBucket: SuperChartConfiguration_TimeBucket): void {
        this._timeBucket = timeBucket
        this._updateCountdown()
    }

    updateTimezone(timezone: TimeZone): void {
        this._timezone = timezone
        this._updateCountdown()
    }

    /** Hide the countdown when the market isn't live (no candle is currently forming). */
    setEnabled(enabled: boolean): void {
        if (this._enabled === enabled) return
        this._enabled = enabled
        this._updateCountdown()
        // Nothing changes while the market is closed, so the 1 s timer only runs while enabled.
        if (this.series) {
            if (enabled) this._startTimer()
            else this._stopTimer()
        }
    }

    private _startTimer(): void {
        if (this._intervalId !== null) return
        this._intervalId = setInterval(() => {
            this._updateCountdown()
        }, 1000)
    }

    private _stopTimer(): void {
        if (this._intervalId !== null) {
            clearInterval(this._intervalId)
            this._intervalId = null
        }
    }

    private _applyTitle(title: string): void {
        if (!this.series || title === this._lastAppliedTitle) return
        this._lastAppliedTitle = title
        this.series.applyOptions({ title })
    }

    applyOptions(options: CountdownPrimitiveOptions): void {
        if (options.textColor !== undefined) this.textColor = options.textColor
        if (options.backColor !== undefined) this.backColor = options.backColor
    }

    attached(param: SeriesAttachedParameter<Time, SeriesType>): void {
        this.series = param.series as ISeriesApi<keyof SeriesOptionsMap>
        this._requestUpdate = param.requestUpdate
        // Preserve whatever title was set before we attach
        this._originalTitle = (param.series.options() as any).title ?? ''
        this._lastAppliedTitle = this._originalTitle
        this._dataChangedHandler = () => {
            this._updateCountdown()
        }
        param.series.subscribeDataChanged(this._dataChangedHandler)
        this._updateCountdown()
        if (this._enabled) this._startTimer()
    }

    detached(): void {
        // Stop the timer first: it is the reference that keeps a destroyed chart alive, so it must
        // be cleared even if the series below has already been disposed and throws.
        this._stopTimer()
        try {
            if (this._dataChangedHandler) {
                this.series?.unsubscribeDataChanged(this._dataChangedHandler)
            }
            // Restore the original title
            this.series?.applyOptions({ title: this._originalTitle })
        } catch {
            /* series already removed with the chart */
        }
        this._dataChangedHandler = null
        this.series = null
        this._requestUpdate = null
        this._lastAppliedTitle = null
    }

    updateAllViews(): void {}

    private _updateCountdown(): void {
        if (!this.series) {
            this.countdownText = ''
            return
        }
        if (!this._enabled) {
            this.countdownText = ''
            this._applyTitle(this._originalTitle)
            return
        }
        // dataByIndex reads one bar; series.data() copies every bar on each call (this ran once a
        // second per chart and was the largest source of idle allocation churn).
        const lastBar = this.series.dataByIndex(Number.MAX_SAFE_INTEGER, MismatchDirection.NearestLeft)
        if (!lastBar) {
            this.countdownText = ''
            this._applyTitle(this._originalTitle)
            return
        }
        const lastBarZoned = lastBar.time as number
        const candleCloseZoned = getCandleCloseZoned(lastBarZoned, this._timeBucket)
        const candleCloseUTC = zonedToUTC(candleCloseZoned, this._timezone)
        const remaining = Math.max(0, candleCloseUTC - Math.floor(Date.now() / 1000))
        this.countdownText = formatCountdown(remaining)
        // Updating `title` rewrites the "name label" that lightweight-charts
        // renders in front of the last-price badge inside the chart pane.
        const newTitle = this._originalTitle
            ? `${this._originalTitle}  ${this.countdownText}`
            : this.countdownText
        this._applyTitle(newTitle)
    }
}
